import React, { useState } from "react";
import { ShieldCheck, Activity, ArrowRight, ChevronRight } from "lucide-react";
import BlogSection from "./BlogSection";
import { BlogPost } from "../types";

interface LandingProps {
  onStart: () => void;
  onBlogClick: (post: BlogPost) => void;
  blogPosts: BlogPost[];
}

const Landing: React.FC<LandingProps> = ({
  onStart,
  onBlogClick,
  blogPosts,
}) => {
  // Logic to handle hero image extension fallback (jpg -> png -> jpeg)
  const [heroSrc, setHeroSrc] = useState("/images/landing-hero.jpg");

  const handleHeroError = () => {
    if (heroSrc.endsWith(".jpg")) {
      setHeroSrc("/images/landing-hero.png");
    } else if (heroSrc.endsWith(".png")) {
      setHeroSrc("/images/landing-hero.jpeg");
    }
    // If it fails after trying all, it stays as broken (or we could add a placeholder)
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 pt-14">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row h-[calc(100vh-3.5rem)] min-h-[600px]">
        {/* Left: Content */}
        <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-24 py-12 relative z-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="px-2 py-1 bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-bold tracking-widest uppercase">
              Clinical Quiz
            </span>
            <div className="h-px w-12 bg-slate-200"></div>
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-6">
            Find the{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
              precise
            </span>{" "}
            moisturizer match.
          </h1>

          <p className="text-lg text-slate-600 mb-10 max-w-md leading-relaxed">
            Based on 5 dermatological parameters. <br />
            Get a personalized scientific analysis in 30 seconds.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-slate-400" />
              <span>Hypoallergenic</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity size={18} className="text-slate-400" />
              <span>Clinically Tested</span>
            </div>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <button
              onClick={onStart}
              className="px-10 py-4 bg-slate-900 text-white text-lg font-bold hover:bg-slate-800 transition-all flex items-center gap-3 shadow-xl shadow-slate-200/50"
            >
              Start Analysis <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/* Right: Hero Image/Visual */}
        <div className="flex-1 bg-slate-50 relative overflow-hidden border-l border-slate-100 group">
          <img
            src={heroSrc}
            alt="Dermatology Lab Background with Cream"
            onError={handleHeroError}
            className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white/10 pointer-events-none"></div>
        </div>
      </div>

      {/* Blog Section */}
      <BlogSection posts={blogPosts} onPostClick={onBlogClick} />

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-slate-100 md:hidden z-50">
        <button
          onClick={onStart}
          className="w-full py-4 bg-orange-600 text-white text-lg font-bold shadow-lg flex items-center justify-center gap-2"
        >
          Start Analysis <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default Landing;
